import styles from "../FileSelector.module.scss";
import React, { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useMemo, useCallback } from "react";
import { endpointsCf } from "../../../../config/componentsConfig";
import useFetchWithMsal from "../../../../hooks/useFetchWithMsal";



interface FileSelectorItemProps {
  index: number;
  getFolderSelectionState: (items: any[]) => 'none' | 'partial' | 'full' | null;
  handleFileSelect: (item: any) => void;
  selectTheseFiles: (item: any) => void;
  toggleFolder: (id: string) => void;
  style?: React.CSSProperties;
  expandedFolders: Set<unknown>;
  selectedFiles: string[];
  selectedMode: string;
  flattenedItems: any[];
  summaryExiste: Map<string, boolean>;
  setDocSummary: (summary: {title: string, long: string, short: string}) => void;
  setShowSummaryModal: (show: boolean) => void;
  page: string;
  filters: any;
  setSelectedFiles: React.Dispatch<React.SetStateAction<never[]>>
  setFiles: React.Dispatch<React.SetStateAction<never[]>>;
  setSummaryExiste: (summaryExiste: Map<string, boolean>) => void;
  setShowDeleteModal: (show: {show: boolean, item: any}) => void;
  setDisplaySummaryModal: (displaySummaryModal: {show: boolean, fileName: string, taskType: string, path: string}) => void;
}




const FileSelectorItem: React.FC<FileSelectorItemProps> = ({  
  index,
  getFolderSelectionState,
  handleFileSelect,
  selectTheseFiles,
  toggleFolder,
  style,
  expandedFolders,
  selectedFiles,
  selectedMode,
  flattenedItems,
  summaryExiste,
  setDocSummary,
  setShowSummaryModal,
  page,
  filters,
  setSelectedFiles,
  setFiles,
  setSummaryExiste,
  setShowDeleteModal,
  setDisplaySummaryModal
}) => {

  const [isHovered, setIsHovered] = useState(false);

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'pdf':
        return `bi-filetype-pdf ${styles.pdfFileIcon}`;
      case 'doc':
      case 'docx':
        return 'bi-file-word text-primary';
      case 'xls':
      case 'xlsx':
        return 'bi-file-excel text-success';
      default:
        return 'bi-file-text';
    }
  };

  /**
       * Composant pour afficher un élément de l'arborescence des fichiers
       */
  const item = flattenedItems[index];
  const isFolderExpanded = item.type === "folder" && expandedFolders.has(item.id);

  const { execute, isLoading} = useFetchWithMsal();

  const folderSelectionState = useMemo(() => {
    if (item.type === 'folder' && item.children) {
      return getFolderSelectionState(item.children);
    }
    return null;
  }, [item, getFolderSelectionState]);

  const handleFileSummary = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedMode !== "MYGEORGES") return;
    
    try {
      const data = await execute("POST", endpointsCf.showSummary, {
        fileId: item.id,
        fileName: item.name,
        page: page,
        selectedMode: selectedMode
      });
      // console.log("summaries", data);
      setDocSummary({title: item.name, long: data.longSummary, short: data.shortSummary});
      setShowSummaryModal(true);
    } catch (err) {
      console.error('Summary error:', err);
    }
  }, [execute, page, item.id, item.name, selectedMode, filters]);

  const openFileDeleteModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteModal({show: true, item: item})
  }

  // Ajout d'un log pour déboguer
  if (selectedMode === "MYGEORGES" && item.type !== 'folder') {
    console.log(`Item ${item.name}: hasSummary = ${summaryExiste.get(item.id)}`);
  }

  return(
    <div style={{ ...style, marginLeft: `${item.level * 20}px` }}>
      {item.type === 'folder' ? (
        <div
          className={`${styles.fileSelectorComponent} ${styles.folderRow}`}
          style={{ width: `calc(100% - ${item.level * 20}px)` }}
        >
          <div className="d-flex align-items-center" style={{ minWidth: "24px" }}>
            <input
              type="checkbox"
              className="form-check-input me-2"
              checked={folderSelectionState === "full"}
              ref={(input) => {
                if (input) {
                  input.indeterminate = folderSelectionState === "partial";
                }
              }}
              onChange={() => handleFileSelect(item)}
              onDoubleClick={() => selectTheseFiles(item)}
            />
          </div>
          <div
            className={styles.folderClickArea}
            onClick={(e) => {
              e.stopPropagation();
              toggleFolder(item.id);
            }}
          >
            <i
              className={`bi bi-chevron-right ${styles.folderChevron} ${
                isFolderExpanded ? styles.folderChevronOpen : ""
              }`}
              aria-hidden
            />
            <i
              className={`bi ${
                isFolderExpanded ? "bi-folder2-open" : "bi-folder-fill"
              } ${styles.folderIcon} ${isFolderExpanded ? styles.folderIconOpen : ""}`}
              aria-hidden
            />
            <span className={styles.folderLabel}>{item.name}</span>
          </div>
        </div>
      ) : (
        <OverlayTrigger
          placement="top"
          delay={{ show: 100, hide: 0 }}
          overlay={
            <Tooltip 
              id={`tooltip-${item.id}`}
              className={styles.tooltip}
              style={{ position: 'absolute' }}
            >
              {item.name}
            </Tooltip>
          }
        >
          <div className={styles.fileSelectorComponent} style={{width: `calc(100% - ${item.level * 20}px)`}} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <div className="d-flex align-items-center" style={{ minWidth: '24px' }}>
              <input
                type="checkbox"
                className="form-check-input me-2"
                checked={selectedFiles.includes(item.id)}
                onChange={() => handleFileSelect(item)}
                onDoubleClick={() => selectTheseFiles(item)}
              />
            </div>
            <div 
              className="d-flex align-items-center flex-grow-1"
              style={{ cursor: 'pointer', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
              onClick={() => handleFileSelect(item)}
              onDoubleClick={() => selectTheseFiles(item)}
            >
              <i className={`bi me-2 ${styles.fileTreeIcon} ${getFileIcon(item.type)}`} />
              
              {item.name}
            </div>
            {selectedMode === "MYGEORGES" && item.type !== 'folder' && isHovered && (
              <div className="d-flex align-items-center">
                {/*summaryExiste.get(item.id) && (
                  <button
                    className={styles.itemActionButton}
                    onClick={handleFileSummary}
                    title="View summary"
                  >
                    <i className="bi bi-file-text"></i>
                  </button>
                )*/}
                <button
                  onClick={openFileDeleteModal}
                  title="Delete file"
                  className={styles.itemActionButton}
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            )}
          {page !== "legal" && item.type !== 'folder' && isHovered && (
              <div className="d-flex align-items-center">
                <button
                  className={styles.itemActionButton}
                  onClick={() => setDisplaySummaryModal({show: true, fileName: item.name, taskType: "summarize", path: item.path})}
                  title="View summary"
                >
                  <i className="bi bi-file-text"></i>
                </button>
              </div>
            )}
          </div>
        </OverlayTrigger>
      )}
    </div>);
}/*, (prevProps, nextProps) => {
const prevItem = flattenedItems[prevProps.index];
const nextItem = flattenedItems[nextProps.index];
return (
  prevItem.id === nextItem.id &&
  prevProps.style === nextProps.style &&
  summaryExiste.get(prevItem.id) === summaryExiste.get(nextItem.id)
);
});*/

export default FileSelectorItem;